import {computed, defineComponent, onMounted, ref} from 'vue';
import {
    Address,
    OrganisationInvoiceGroup,
    OrganisationInvoiceGroupMember,
    OrganisationInvoiceSetting
} from "@/api/klip-api.proxy";
import {useKlipApiProxy} from "@/plugins/proxy-client";
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {escape} from "html-escaper";
import {useKlModalEventsUtil} from "@/app/shared/helpers/kl-modal-events-util";
import _default from "ol/CollectionEventType";
import ADD = _default.ADD;

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlSettingsInvoice',
    props: {},
    setup() {
        const deleteInvoiceGroupModalEvents = useKlModalEventsUtil('delete-invoice-group', {
            onClosed() {
                organisationInvoiceGroupToDelete.value = null;
                deletingOrganisationInvoiceGroup.value = false;
            },
        });
        const subOrganisations = useUserStore().user.subOrganisations;
        const helpdeskMail: string = import.meta.env.VITE_EMAIL_HELPDESK;
        const showAddress = ref<boolean>(false);
        const showExtraInfo = ref<boolean>(true);
        const isLoading = ref<boolean>(true);
        const deletingOrganisationInvoiceGroup = ref<boolean>(false);
        const organisationInvoiceSettings = ref<OrganisationInvoiceSetting>();
        const subOrganisationsInvoiceSetting = ref<OrganisationInvoiceSetting[]>([]);
        const organisationInvoiceSettingToUpdate = ref<OrganisationInvoiceSetting>(new OrganisationInvoiceSetting({}));
        const organisationInvoiceGroups = ref<OrganisationInvoiceGroup[]>([]);
        const organisationInvoiceGroupToDelete = ref<OrganisationInvoiceGroup>(null);
        const organisationInvoiceGroupToUpdate = ref<OrganisationInvoiceGroup>(new OrganisationInvoiceGroup({}));
        const organisationsAlreadyInGroup = ref<OrganisationInvoiceGroupMember[]>([]);

        const user = computed(() => {
            return useUserStore().user;
        });

        const hasSubOrganisations = computed(() => {
            return user.value.subOrganisations.some(o => o.id !== user.value.klipOrganisationApiId);
        });

        const organisationEmail = computed(()=> {
            return organisationInvoiceSettings.value && organisationInvoiceSettings.value.email ? organisationInvoiceSettings.value.email : user.value.email;
        });

        const name = computed(() => {
            return organisationInvoiceSettings.value && organisationInvoiceSettings.value.company ? organisationInvoiceSettings.value.company :
                user.value.organisation ? user.value.organisation : '';
        });

        const organisationAddress = computed(() => {
            const invoiceData = organisationInvoiceSettings.value;
            const streetParts = `${[invoiceData.address.street, invoiceData.address.streetNumber, invoiceData.address.box].join(' ').trim()}`;
            const cityParts = `${[invoiceData.address.zip, invoiceData.address.city].join(' ').trim()}
                ${invoiceData.address.city && invoiceData.address.country ?
                ` - ${invoiceData.address.country}` : ''}`;
            return `${escape(streetParts)}${streetParts && cityParts ? '<br>' : ''}${escape(cityParts)}`.trim();
        });

        const organisationDisplayName = computed(() => {
            return organisationInvoiceSettings.value.kboNumber ? `${escape(name.value)} - ${organisationInvoiceSettings.value.kboNumber}` : escape(name.value);
        });

        const organisationVatNumber = computed(() => {
            return organisationInvoiceSettings.value ? `BTW ${escape(organisationInvoiceSettings.value.vatNumber)}` : '';
        });

        const loadOrganisationInvoiceSettings = async () => {
            const response = await useKlipApiProxy().settings_GetOrganisationInvoiceSettings();
            organisationInvoiceSettings.value = response.result.organisationInvoiceSetting;
            subOrganisationsInvoiceSetting.value = response.result.subOrganisationsInvoiceSetting;
            organisationInvoiceGroups.value = response.result.organisationInvoiceGroups;
            organisationsAlreadyInGroup.value = response.result.organisationInvoiceGroups?.map(o => o.organisations).flat();
            isLoading.value = false;
        }

        const saveOrganisationInvoiceGroup = async (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            if(!organisationInvoiceGroup.id) {
                organisationInvoiceGroup.parentOrganisationId = user.value.klipOrganisationApiParentId;
                await useKlipApiProxy().settings_CreateOrganisationInvoiceGroup(organisationInvoiceGroup);
            }else {
                await useKlipApiProxy().settings_UpdateOrganisationInvoiceGroup(organisationInvoiceGroup);
            }
            await loadOrganisationInvoiceSettings();
        };

        const cancelOrganisationInvoiceGroup = () => {
            organisationsAlreadyInGroup.value = organisationInvoiceGroups.value.map(o => o.organisations).flat();
        }

        const updateOrganisationInvoiceGroup = (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            organisationInvoiceGroupToUpdate.value = organisationInvoiceGroup;
            organisationsAlreadyInGroup.value = organisationInvoiceGroups.value.map(o => o.organisations).flat()
                .filter(org => organisationInvoiceGroup.organisations.some(o => o.memberKlipOrganisationId !== org.memberKlipOrganisationId))
                .flat()
        };

        const showDeleteOrganisationInvoiceGroupModal = (organisationInvoiceGroup: OrganisationInvoiceGroup) => {
            organisationInvoiceGroupToDelete.value = organisationInvoiceGroup;
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const deleteOrganisationInvoiceGroup = async () => {
            deletingOrganisationInvoiceGroup.value = true;
            await useKlipApiProxy().settings_DeleteOrganisationInvoiceGroup(organisationInvoiceGroupToDelete.value.id);
            await loadOrganisationInvoiceSettings();
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const cancelDeleteOrganisationInvoiceGroup = () => {
            deleteInvoiceGroupModalEvents.triggerModalToggle();
        };

        const updateOrganisationInvoiceSettings = (organisationInvoiceSetting: OrganisationInvoiceSetting) => {
            showAddress.value = true;
            showExtraInfo.value = !hasSubOrganisations.value;
            organisationInvoiceSettingToUpdate.value = OrganisationInvoiceSetting.fromJS(organisationInvoiceSetting);
        }

        const updateSubOrganisationInvoiceSettings = (organisationInvoiceSetting: OrganisationInvoiceSetting) => {
            showAddress.value = false;
            showExtraInfo.value = true;
            organisationInvoiceSettingToUpdate.value = OrganisationInvoiceSetting.fromJS(organisationInvoiceSetting);
        }

        const updateInvoiceSettingFields = (target: OrganisationInvoiceSetting, source: OrganisationInvoiceSetting) => {
            target.attention = source.attention;
            target.reference = source.reference;
            target.email = source.email;
            target.reminderEmail = source.reminderEmail;
        };

        const saveOrganisationInvoiceSettings = async (organisationInvoiceSettingToSave: OrganisationInvoiceSetting) => {
            await useKlipApiProxy().settings_UpdateOrganisationSettings(organisationInvoiceSettingToSave);

            const isMainOrg = organisationInvoiceSettingToSave.klipOrganisationId === organisationInvoiceSettings.value?.klipOrganisationId;
            const organisationInvoiceSettingToUpdate = isMainOrg
                ? organisationInvoiceSettings.value
                : subOrganisationsInvoiceSetting.value.find(o => o.klipOrganisationId === organisationInvoiceSettingToSave.klipOrganisationId);

            updateInvoiceSettingFields(organisationInvoiceSettingToUpdate, organisationInvoiceSettingToSave);

            await loadOrganisationInvoiceSettings();
        };

        const saveAddress = async (addressToSave: Address) => {

        }

        onMounted(async () => {
            await loadOrganisationInvoiceSettings();
        });

        return {
            helpdeskMail,
            isLoading,
            showAddress,
            showExtraInfo,
            deletingOrganisationInvoiceGroup,
            hasSubOrganisations,
            organisationInvoiceSettings,
            organisationInvoiceGroups,
            organisationsAlreadyInGroup,
            organisationDisplayName,
            organisationVatNumber,
            organisationAddress,
            organisationEmail,
            subOrganisationsInvoiceSetting,
            saveOrganisationInvoiceGroup,
            cancelOrganisationInvoiceGroup,
            updateOrganisationInvoiceGroup,
            deleteOrganisationInvoiceGroup,
            cancelDeleteOrganisationInvoiceGroup,
            showDeleteOrganisationInvoiceGroupModal,
            updateOrganisationInvoiceSettings,
            updateSubOrganisationInvoiceSettings,
            saveOrganisationInvoiceSettings,
            organisationInvoiceGroupToUpdate,
            organisationInvoiceSettingToUpdate,
            subOrganisations
        }
    }
})
